import { css } from '@firebolt-dev/css'
import { PlusIcon, SaveIcon, XIcon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { UIEditorFieldText } from './UIEditorFieldText'
import { produce } from 'immer'
import { UIEditorFieldAsset } from './UIEditorFieldAsset'
import { UIEditorFieldNumber } from './UIEditorFieldNumber'
import { UIEditorFieldToggle } from './UIEditorFieldToggle'

const types = {
  sound: {
    name: 'sound',
    title: 'Sound',
  },
}

export function UIInspector({ client, asset: initialAsset, onChangeID, onClose, showModal }) {
  const [asset, setAsset] = useState(initialAsset)

  useEffect(() => {
    console.log('UIInspector asset change')
    setAsset(initialAsset)
  }, [initialAsset])

  const edit = fn => {
    setAsset(asset => {
      return produce(asset, draft => {
        fn(draft)
      })
    })
  }

  const save = async () => {
    // if ID changes we need to check its available
    const newID = initialAsset.id !== asset.id ? asset.id : null
    if (newID) {
      const taken = client.registry.get(asset.type, newID)
      if (taken) {
        showModal({
          title: 'ID Taken',
          message: 'An asset with this ID already exists',
          btns: [
            {
              label: 'Okay',
              action: null,
            },
          ],
        })
        return
      }
      const ops = {
        remove: [{ type: initialAsset.type, id: initialAsset.id }],
        upsert: [asset],
      }
      client.network.send('editAssets', ops)
      onChangeID(newID)
    } else {
      const ops = {
        remove: [],
        upsert: [asset],
      }
      client.network.send('editAssets', ops)
    }
  }

  return (
    <div
      className='inspector noscrollbar'
      css={css`
        background: var(--bg3);
        border-radius: 5px;
        padding: 15px;
        overflow-y: auto;
        .inspector-title {
          display: flex;
          align-items: center;
          margin: 0 0 20px;
          span {
            flex: 1;
            font-size: 21px;
            font-weight: 500;
          }
        }
        .inspector-btn {
          width: 34px;
          height: 34px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 5px;
          &:hover {
            cursor: pointer;
            background: var(--bg4);
          }
        }
        .inspector-section {
          margin: 20px 0 10px 0;
          font-weight: 500;
          font-size: 18px;
        }
        .inspector-control {
          display: flex;
          align-items: center;
          margin: 0 0 5px;
          min-height: 34px;
          &-label {
            width: 120px;
            color: rgba(255, 255, 255, 0.6);
          }
          &-field {
            flex: 1;
          }
          &-btns {
            display: flex;
            justify-content: flex-end;
            gap: 5px;
          }
          &-btn {
            width: 34px;
            height: 34px;
            background: var(--bg4);
            border-radius: 5px;
            display: flex;
            align-items: center;
            justify-content: center;
            &:hover {
              cursor: pointer;
              background: var(--bg5);
            }
          }
        }
      `}
    >
      <div className='inspector-title'>
        <span>{types[asset.type].title}</span>
        <div className='inspector-btn' onClick={save}>
          <SaveIcon size={20} />
        </div>
        <div className='inspector-btn' onClick={onClose}>
          <XIcon size={20} />
        </div>
      </div>
      <div className='inspector-control'>
        <div className='inspector-control-label'>ID</div>
        <div className='inspector-control-field'>
          <UIEditorFieldText
            value={asset.id}
            onChange={value => {
              edit(asset => {
                asset.id = value
              })
            }}
          />
        </div>
      </div>
      {asset.type === 'sound' && <Sound asset={asset} edit={edit} />}
    </div>
  )
}

function Sound({ asset, edit }) {
  return (
    <>
      <div className='inspector-control'>
        <div className='inspector-control-label'>Spatial</div>
        <div className='inspector-control-field'>
          <UIEditorFieldToggle
            value={asset.spatial}
            onChange={value => {
              edit(asset => {
                asset.spatial = value
              })
            }}
          />
        </div>
      </div>
      <div className='inspector-control'>
        <div className='inspector-control-label'>Pitch Rand</div>
        <div className='inspector-control-field'>
          <UIEditorFieldNumber
            dp={1}
            min={0}
            step={0.1}
            bigStep={0.1}
            littleStep={0.1}
            value={asset.pitchRandom}
            onChange={value => {
              edit(asset => {
                asset.pitchRandom = value
              })
            }}
          />
        </div>
      </div>
      <div className='inspector-control'>
        <div className='inspector-control-label'>Volume Rand</div>
        <div className='inspector-control-field'>
          <UIEditorFieldNumber
            dp={1}
            min={0}
            step={0.1}
            bigStep={0.1}
            littleStep={0.1}
            value={asset.volumeRandom}
            onChange={value => {
              edit(asset => {
                asset.volumeRandom = value
              })
            }}
          />
        </div>
      </div>
      <div className='inspector-control'>
        <div className='inspector-control-label'>Variants</div>
        <div className='inspector-control-field'></div>
      </div>
      {asset.variants.map((variant, idx) => (
        <div key={idx}>
          <div className='inspector-control'>
            <div className='inspector-control-label'>Sound</div>
            <div className='inspector-control-field'>
              <UIEditorFieldAsset
                registry={client.registry}
                type='audio'
                value={asset.variants[idx].id}
                onChange={value => {
                  edit(asset => {
                    asset.variants[idx].id = value
                  })
                }}
              />
            </div>
          </div>
          <div className='inspector-control'>
            <div className='inspector-control-label'>Volume</div>
            <div className='inspector-control-field'>
              <UIEditorFieldNumber
                dp={1}
                min={0.1}
                step={0.1}
                bigStep={0.1}
                littleStep={0.1}
                value={asset.variants[idx].volume}
                onChange={value => {
                  edit(asset => {
                    asset.variants[idx].volume = value
                  })
                }}
              />
            </div>
          </div>
          <div className='inspector-control'>
            <div className='inspector-control-label'>Pitch</div>
            <div className='inspector-control-field'>
              <UIEditorFieldNumber
                dp={1}
                min={0.1}
                step={0.1}
                bigStep={0.1}
                littleStep={0.1}
                value={asset.variants[idx].pitch}
                onChange={value => {
                  edit(asset => {
                    asset.variants[idx].pitch = value
                  })
                }}
              />
            </div>
          </div>
          <div className='inspector-control'>
            <div className='inspector-control-label'>Weight</div>
            <div className='inspector-control-field'>
              <UIEditorFieldNumber
                dp={0}
                min={1}
                step={1}
                bigStep={1}
                littleStep={1}
                value={asset.variants[idx].weight}
                onChange={value => {
                  edit(asset => {
                    asset.variants[idx].weight = value
                  })
                }}
              />
            </div>
          </div>
          <div className='inspector-control'>
            <div className='inspector-control-label'></div>
            <div className='inspector-control-field'>
              <div className='inspector-control-btns'>
                <div
                  className='inspector-control-btn'
                  onClick={() => {
                    edit(asset => {
                      asset.variants.splice(idx + 1, 0, { id: null, volume: 1, pitch: 1, weight: 1 })
                    })
                  }}
                >
                  <PlusIcon size={16} />
                </div>
                <div
                  className='inspector-control-btn'
                  onClick={() => {
                    edit(asset => {
                      asset.variants.splice(idx, 1)
                    })
                  }}
                >
                  <XIcon size={16} />
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  )
}
